import { useMountEffect } from './useMountEffect'

/**
 * Extra clearance (px) above the keyboard for iOS Safari's floating AutoFill /
 * keyboard-dismiss controls. These overlay web content just above the keyboard
 * and are NOT reported by the VisualViewport API, so bottom-pinned controls
 * that sit flush against the keyboard get covered by them. Padding by this much
 * lets those controls float in the gap below our buttons instead of over them.
 * Device-tuned — adjust if iOS changes the control band height.
 */
const KEYBOARD_ACCESSORY_CLEARANCE_PX = 96

/**
 * iOS is the only platform with floating AutoFill / keyboard-dismiss controls
 * that overlay content just above the keyboard. Android's keyboard has no such
 * band, so the accessory clearance would only add dead space there. (iPadOS 13+
 * reports as "Mac" with a touch screen, hence the maxTouchPoints check.)
 */
const isIOS =
  typeof navigator !== 'undefined' &&
  (/iP(hone|ad|od)/.test(navigator.platform) ||
    (navigator.userAgent.includes('Mac') && navigator.maxTouchPoints > 1))

/**
 * Tracks the on-screen keyboard on mobile via the VisualViewport API and exposes
 * two CSS custom properties on `<html>`:
 *
 * - `--keyboard-inset-height` — how much of the layout viewport the keyboard
 *   covers (`0px` when closed).
 * - `--keyboard-accessory-clearance` — extra room for iOS's floating AutoFill /
 *   dismiss controls ({@link KEYBOARD_ACCESSORY_CLEARANCE_PX} when the keyboard
 *   is open on iOS, `0px` otherwise — Android has no such control band).
 *
 * On iOS Safari (and Android Chrome's default `resizes-visual` mode) the soft
 * keyboard shrinks the *visual* viewport but NOT the layout viewport, so
 * `100dvh` / `vh` / `%` heights keep their full size and any bottom-pinned UI
 * ends up hidden behind the keyboard. `window.resize` / `innerHeight` don't
 * react to the keyboard on iOS either — only `window.visualViewport` does.
 *
 * Consumers pad or pin against these vars so their bottom controls stay above
 * both the keyboard and its floating controls. Both are removed on unmount.
 */
export function useKeyboardInset() {
  useMountEffect(() => {
    const vv = window.visualViewport
    if (!vv) return

    const update = () => {
      // Layout viewport (window.innerHeight) stays constant when the keyboard
      // opens; the visual viewport shrinks. The difference is the covered area.
      const inset = Math.max(0, window.innerHeight - vv.height - vv.offsetTop)
      const accessory = inset > 0 && isIOS ? KEYBOARD_ACCESSORY_CLEARANCE_PX : 0
      const root = document.documentElement.style
      root.setProperty('--keyboard-inset-height', `${inset}px`)
      root.setProperty('--keyboard-accessory-clearance', `${accessory}px`)
    }

    update()
    vv.addEventListener('resize', update)
    vv.addEventListener('scroll', update)

    return () => {
      vv.removeEventListener('resize', update)
      vv.removeEventListener('scroll', update)
      const root = document.documentElement.style
      root.removeProperty('--keyboard-inset-height')
      root.removeProperty('--keyboard-accessory-clearance')
    }
  })
}
